<template>
    <div class="cache-attribute-picker">
        <div class="filter-menu">
            <SupsisButton v-if="selectedMenu.length" ghost small @click="prev">
                <span class="material-icons"> chevron_left </span>
            </SupsisButton>
            <div class="breadcrumb-item" v-for="menu in selectedMenu" :key="menu">
                <span> {{ findAttributeByValue(menu).label }} ✦ </span>
            </div>
        </div>

        <div class="cache-blocks" v-if="!selectedMenu.length">
            <div class="block-item" v-for="block in cacheBlocks" :key="block.id" @click="selectBlock(block)">
                <span class="material-icons">{{ getBlockIcon(block.type) }}</span>
                <span class="name">{{ block.name || block.type }}</span>
            </div>
        </div>

        <div class="attribute-list" v-else>
            <div class="attr-select">
                <div
                    class="picker-item"
                    v-for="attr in filterAttributes()"
                    @click="() => select(attr.value)"
                    :key="attr.value"
                >
                    <span class="name">{{ attr.label }}</span>
                    <span class="description" v-if="attr.description">{{ attr.description }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import flatDiagram from "../../../utils/flatDiagram";
import { mapGetters } from "vuex";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

export default {
    name: "CacheAttributePicker",
    components: {
        SupsisButton,
    },
    props: {
        onPicked: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            cacheBlocks: [],
            attributes: [],
            selectedMenu: [],
            selectedBlock: null,
            searchText: "",
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
    },
    methods: {
        listCacheBlocks() {
            const storyId = this.$route.params.id;
            const board = this.board(storyId);
            
            if (!board || !board.diagram) {
                return [];
            }
            
            const flatted = flatDiagram(board.diagram);
            const cacheBlockTypes = ["CACHE_WRITE", "CACHE_READ", "CACHE_EXTEND", "CACHE_DELETE"];
            return flatted.filter((block) => cacheBlockTypes.includes(block.type));
        },

        selectBlock(block) {
            this.selectedBlock = block;
            this.attributes = this.generateAttributesForBlock(block);
            this.selectedMenu.push("root");
        },

        getBlockIcon(blockType) {
            const icons = {
                CACHE_WRITE: "save",
                CACHE_READ: "download",
                CACHE_EXTEND: "update",
                CACHE_DELETE: "delete_forever",
            };
            return icons[blockType] || "storage";
        },

        generateAttributesForBlock(block) {
            const blockId = block.id;
            const prefix = `block.${blockId}`;

            const attributes = [
                {
                    label: "*",
                    value: `${prefix}`,
                    description: "Tüm değerler",
                },
                {
                    label: "success",
                    value: `${prefix}.success`,
                    description: "İşlem başarılı mı? (boolean)",
                },
            ];

            if (block.type === "CACHE_WRITE") {
                attributes.push(
                    {
                        label: "key",
                        value: `${prefix}.key`,
                        description: "Cache key (string)",
                    },
                    {
                        label: "cacheId",
                        value: `${prefix}.cacheId`,
                        description: "Cache document ID (string)",
                    },
                    {
                        label: "expiresAt",
                        value: `${prefix}.expiresAt`,
                        description: "Geçerlilik tarihi (date)",
                    },
                    {
                        label: "error",
                        value: `${prefix}.error`,
                        description: "Hata mesajı (string)",
                    }
                );
            } else if (block.type === "CACHE_READ") {
                attributes.push(
                    {
                        label: "key",
                        value: `${prefix}.key`,
                        description: "Cache key (string)",
                    },
                    {
                        label: "value",
                        value: `${prefix}.value`,
                        description: "Cache değeri (mixed)",
                    },
                    {
                        label: "found",
                        value: `${prefix}.found`,
                        description: "Cache bulundu mu? (boolean)",
                    },
                    {
                        label: "error",
                        value: `${prefix}.error`,
                        description: "Hata mesajı (string)",
                    }
                );
            } else if (block.type === "CACHE_EXTEND") {
                attributes.push(
                    {
                        label: "key",
                        value: `${prefix}.key`,
                        description: "Cache key (string)",
                    },
                    {
                        label: "expiresAt",
                        value: `${prefix}.expiresAt`,
                        description: "Yeni geçerlilik tarihi (date)",
                    },
                    {
                        label: "error",
                        value: `${prefix}.error`,
                        description: "Hata mesajı (string)",
                    }
                );
            } else if (block.type === "CACHE_DELETE") {
                attributes.push(
                    {
                        label: "key",
                        value: `${prefix}.key`,
                        description: "Cache key/pattern (string)",
                    },
                    {
                        label: "deletedCount",
                        value: `${prefix}.deletedCount`,
                        description: "Silinen sayısı (number)",
                    },
                    {
                        label: "pattern",
                        value: `${prefix}.pattern`,
                        description: "Pattern kullanıldı mı? (boolean)",
                    },
                    {
                        label: "error",
                        value: `${prefix}.error`,
                        description: "Hata mesajı (string)",
                    }
                );
            }

            return attributes;
        },

        filterAttributes() {
            const parentAttribute = this.getParentAttribute();
            let attrs = parentAttribute ? parentAttribute.subAttributes : this.attributes;

            return attrs.filter((attr) => {
                if (this.searchText) {
                    return (attr.label || "").toLowerCase().includes(this.searchText.toLowerCase());
                }
                return true;
            });
        },

        getParentAttribute() {
            if (this.selectedMenu.length <= 1) return null;
            const attributes = this.getFlatAttributes();
            const parent = this.selectedMenu[this.selectedMenu.length - 1];
            return attributes.find((x) => x.value === parent);
        },

        getFlatAttributes() {
            const flat = (attr) => {
                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap(flat)];
                }
                return [attr];
            };
            return this.attributes.flatMap(flat);
        },

        select(value) {
            const attributes = this.getFlatAttributes();
            const attr = attributes.find((x) => x.value === value);
            if (Array.isArray(attr.subAttributes) && attr.subAttributes.length) {
                this.selectedMenu.push(value);
            } else {
                this.onPicked(value);
            }
        },

        prev() {
            if (this.selectedMenu.length === 1) {
                this.selectedMenu = [];
                this.selectedBlock = null;
            } else {
                this.selectedMenu.pop();
            }
        },

        findAttributeByValue(value) {
            if (value === "root") {
                return { label: this.selectedBlock?.name || "Cache Block" };
            }
            const attributes = this.getFlatAttributes();
            return attributes.find((x) => x.value === value) || { label: value };
        },
    },
    created() {
        this.cacheBlocks = this.listCacheBlocks();
    },
};
</script>

<style lang="scss" scoped>
.cache-attribute-picker {
    background: #fff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    max-height: 600px;
    min-height: 200px;
    user-select: none;
    width: 100%;
    padding: 5px;

    .filter-menu {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-bottom: 10px;

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }

        .breadcrumb-item {
            display: flex;
        }
    }

    .cache-blocks {
        .block-item {
            display: flex;
            align-items: center;
            padding: 8px;
            cursor: pointer;
            border-radius: 4px;
            margin-bottom: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .material-icons {
                margin-right: 8px;
                color: #1976d2;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }
        }
    }

    .attribute-list {
        flex-grow: 1;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 5px;
        margin: 0px 5px 0px 5px;

        .picker-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            padding: 8px;
            transition: background-color 0.2s;
            border-radius: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }

            .description {
                color: #9ba6b3;
                font-size: 12px;
            }
        }
    }
}
</style>

